import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface TradeStats {
  totalTraders: number;
  totalTrades: number;
  wins: { count: number; totalAmount: number };
  losses: { count: number; totalAmount: number };
}

export function AdminTrades() {
  const [stats, setStats] = useState<TradeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getAdminTradeStats();
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load trade stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 3600000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const formatNumber = (n: number) => n.toLocaleString('ru-RU');

  if (loading && !stats) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      padding: '16px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Trade Statistics</h2>
        <button
          onClick={loadStats}
          disabled={loading}
          style={{
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '6px 14px',
            color: '#3b82f6',
            fontSize: '13px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          {loading ? 'Updating...' : 'Refresh'}
        </button>
      </div>

      {lastUpdated && (
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>
          Last updated: {lastUpdated.toLocaleTimeString('ru-RU')} (auto-refresh every hour)
        </div>
      )}

      {stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
              Real Traders
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#3b82f6' }}>
              {formatNumber(stats.totalTraders)}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              Total trades: {formatNumber(stats.totalTrades)}
            </div>
          </div>

          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '12px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
              Total Won by Users
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
              {formatNumber(stats.wins.totalAmount)} WiTon
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              Win trades: {formatNumber(stats.wins.count)}
            </div>
          </div>

          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
              Total Lost by Users
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#ef4444' }}>
              {formatNumber(stats.losses.totalAmount)} WiTon
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              Loss trades: {formatNumber(stats.losses.count)}
            </div>
          </div>

          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '12px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
              Net Platform Profit
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#f59e0b' }}>
              {formatNumber(stats.losses.totalAmount - stats.wins.totalAmount)} WiTon
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              Win rate: {stats.totalTrades > 0 ? ((stats.wins.count / stats.totalTrades) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
